/**
 * The Labs event vocabulary: every event name an app may send, and the props
 * each one carries.
 *
 * It is deliberately closed and deliberately small. Analytics stops being
 * useful the moment two apps measure the same moment under different names, so
 * `trackEvent` takes a name from this union and refuses to compile with
 * anything else. Adding a name is a considered edit to this file, reviewed like
 * any other shared contract — not something an app invents on its own.
 *
 * Most of it costs an app nothing. Core fires `app_opened` from `createCore`,
 * and `postToServer` fires the three capability events for every endpoint a
 * capability declares. What is left is the handful of moments only the app can
 * see, listed under "Creative workflow" below.
 *
 * Prop shapes are `type` aliases rather than interfaces on purpose, against the
 * repo's usual preference: only a type alias of an object literal gets
 * TypeScript's implicit index signature, and without it none of these shapes
 * satisfies `TelemetryProps` — the `Record<string, string | number | boolean>`
 * the wire accepts. An explicit index signature would be the other way out and
 * a worse one, since it would let any key through and give up the checking this
 * file exists for.
 */
/* eslint-disable @typescript-eslint/consistent-type-definitions */

/** An event that carries nothing of its own beyond the standard envelope. */
export type EmptyEventProps = Record<string, never>;

/** The three events `postToServer` reports for every capability call. */
export type CapabilityEventName =
    | "capability_requested"
    | "capability_succeeded"
    | "capability_failed";

/**
 * Taken straight from the `ApiEndpoint` being called, so every capability
 * reports itself the same way without an app naming anything:
 * `capability` is the endpoint's label ("Variations 3.0"), `source` its
 * backend ("Modal", "Atlantis"), `category` its family ("SpliceGen v3.0",
 * "3P GenAI"). `category` is what makes "which models earn their keep"
 * answerable without a lookup table in Amplitude.
 *
 * One shape for all three events rather than three near-identical ones: the
 * two extra fields are documented by which event carries them, and a uniform
 * shape is what lets the transport report them through a plain function
 * instead of a generic one.
 */
export type CapabilityEventProps = {
    capability: string;
    source: string;
    category: string;
    /** On the two outcomes; absent on `capability_requested`. */
    duration_ms?: number;
    /**
     * On `capability_failed` only. Truncated — enough to group failures, not
     * to reconstruct what the user fed the tool.
     */
    error?: string;
};

/** How audio arrived in the app. */
export type InputMethod =
    /** Through a dropzone — dragged in, or its click-to-load picker. */
    | "drop"
    /** Picked from the Splice catalog. */
    | "catalog"
    /** Recorded in the browser. */
    | "recording"
    /** A previous generation fed back in. */
    | "result";

export type InputLoadedProps = {
    method: InputMethod;
};

/** How a result left Labs. The strongest signal that a tool was worth using. */
export type ExportMethod =
    /** Browser download (web). */
    | "download"
    /** Native drag into the DAW (plugin surfaces). */
    | "drag"
    /** Revealed in Finder / Explorer. */
    | "reveal";

export type ResultExportedProps = {
    capability: string;
    method: ExportMethod;
};

/**
 * A generated result the user actually played.
 *
 * The step between "a model returned something" and "they kept it": without
 * it, a result that was never listened to and a result that was heard and
 * rejected look identical in the funnel. Reported once per result rather than
 * once per press — a second play is the same audition, and scrubbing a
 * waveform would otherwise read as enthusiasm.
 */
export type ResultAuditionedProps = {
    capability: string;
};

/** Mirrors the thumb the user pressed in the output-feedback control. */
export type ResultRatedProps = {
    capability: string;
    sentiment: "up" | "down";
};

/**
 * Name → props. The keys of this type ARE the vocabulary; `TelemetryEventName`
 * is derived from it so the two can never drift.
 */
export interface TelemetryEventProps {
    // Lifecycle — fired by core. Apps do nothing.
    app_opened: EmptyEventProps;

    // Capability calls — fired by `postToServer`. Apps do nothing.
    capability_requested: CapabilityEventProps;
    capability_succeeded: CapabilityEventProps;
    capability_failed: CapabilityEventProps;

    // Creative workflow — only the app knows these, so the app fires them.
    // With the capability events they describe one pass through a tool: audio
    // in, generate, take the result to the DAW, say whether it was any good.
    // Two calls of app code, and every Labs app becomes comparable to every
    // other one.
    //
    // `result_auditioned` and `result_rated` are already sent for you by the
    // shared audio component and the shared output-feedback control, so in
    // practice an app writes `input_loaded` and `result_exported` and nothing
    // else.
    input_loaded: InputLoadedProps;
    result_auditioned: ResultAuditionedProps;
    result_exported: ResultExportedProps;
    result_rated: ResultRatedProps;
}

export type TelemetryEventName = keyof TelemetryEventProps;

/**
 * The arguments for one event: its name, plus its props unless the event has
 * none of its own.
 *
 * This is what lets `trackEvent("app_opened")` stand alone while
 * `trackEvent("input_loaded")` will not compile — an event's props are part of
 * its definition above, not something a caller decides. Shared by every
 * function that sends an event so their signatures cannot drift.
 */
export type TelemetryEventArgs<N extends TelemetryEventName> =
    Record<string, never> extends TelemetryEventProps[N]
        ? [name: N, props?: undefined]
        : [name: N, props: TelemetryEventProps[N]];

/**
 * Every name, for runtime use (the vocabulary test, and anything that needs to
 * enumerate events). Kept in sync with `TelemetryEventProps` by the
 * `satisfies` clause: a name added to the interface and forgotten here fails
 * the `kTelemetryEventNames` completeness test in the sibling test file.
 */
export const kTelemetryEventNames = [
    "app_opened",
    "capability_requested",
    "capability_succeeded",
    "capability_failed",
    "input_loaded",
    "result_auditioned",
    "result_exported",
    "result_rated",
] as const satisfies readonly TelemetryEventName[];

/**
 * Failure messages are grouped in Amplitude, not read as prose, and they can
 * quote a filename the user chose. One line is plenty.
 */
export const kMaxErrorPropLength = 200;

export const truncateErrorProp = (message: string): string =>
    message.length <= kMaxErrorPropLength ? message : message.slice(0, kMaxErrorPropLength);
